import { bunja } from 'bunja';
import { differenceInDays } from 'date-fns';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';

import { getVouchersApi, postVoucherActivateApi } from '@flipster/apis/v1/voucher';
import { FeeVoucherGrant, SemanticVoucherStatus, VipVoucherGrant, VoucherType } from '@flipster/apis/v1/voucher/types';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { timestampToDate } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';
import Num from '@flipster/utils/num';
import { gte } from '@flipster/utils/version';

import { authBunja } from '../../../shared/auth/state';
import { webviewInfoBunja } from '../../../shared/webview';

export const voucherQueryKey = ['getVouchersApi'];

const activeStatuses: SemanticVoucherStatus[] = ['ACTIVE', 'INACTIVE'];
const pastStatuses: SemanticVoucherStatus[] = ['PAST'];

export const vouchersBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const { appVersionAtom, webviewAtom } = bunja.use(webviewInfoBunja);

  const showVoucherEntryAtom = atom((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    const appVersion = get(appVersionAtom);
    const webview = get(webviewAtom);
    return isCookieLoggedIn && (gte(appVersion, '3.7.1') || !webview);
  });

  const {
    vouchersAtom: activeVouchersAtom,
    feeTotalCountAtom: activeFeeVoucherCountAtom,
    vipVouchersAtom: activeVipVouchersAtom,
  } = bunja.use(activeVouchersBunja);

  const activeVipVoucherAtom = atom((get) => get(activeVipVouchersAtom).find((v) => v.status === 'ACTIVE'));
  const inactiveVipVoucherAtom = atom((get) => get(activeVipVouchersAtom).find((v) => v.status === 'INACTIVE'));
  const activeVouchersCountAtom = atom((get) => get(activeVouchersAtom).length);

  return {
    showVoucherEntryAtom,
    activeVipVoucherAtom,
    inactiveVipVoucherAtom,
    activeVouchersCountAtom,
    activeFeeVoucherCountAtom,
  };
});
vouchersBunja.debugLabel = 'vouchersBunja';

export const activeVouchersBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const vouchersBaseAtom = atomWithQuery((get) => ({
    queryKey: [...voucherQueryKey, 'active'],
    queryFn: () => getVouchersApi({ status: activeStatuses }),
    enabled: Boolean(get(isCookieLoggedInAtom)),
  }));

  const isLoadingAtom = atom((get) => get(vouchersBaseAtom).isLoading);
  const vouchersAtom = atom((get) => get(vouchersBaseAtom).data?.data.vouchers ?? []);
  const feeVouchersAtom = atom((get) => {
    const vouchers = get(vouchersAtom).filter((v): v is FeeVoucherGrant => v.voucherType === 'FEE');
    return [...vouchers.filter((v) => v.status === 'ACTIVE'), ...vouchers.filter((v) => v.status === 'INACTIVE')];
  });
  const vipVouchersAtom = atom((get) => {
    const vouchers = get(vouchersAtom).filter((v): v is VipVoucherGrant => v.voucherType === 'VIP');
    return [...vouchers.filter((v) => v.status === 'ACTIVE'), ...vouchers.filter((v) => v.status === 'INACTIVE')];
  });
  const feeTotalCountAtom = atom((get) => get(feeVouchersAtom).length);
  const vipTotalCountAtom = atom((get) => get(vipVouchersAtom).length);

  const onlyActiveFeeVouchersAtom = atom((get) => get(feeVouchersAtom).filter((v) => v.status === 'ACTIVE'));

  const perpFeeVoucherAvailableAmountAtom = atomWithBigNumber((get) =>
    get(onlyActiveFeeVouchersAtom)
      .filter((v) => v.applicableProducts?.includes('PERP'))
      .reduce((acc, v) => acc.plus(Num(v.remainingAmount)), Num(0)),
  );
  const spotFeeVoucherAvailableAmountAtom = atomWithBigNumber((get) =>
    get(onlyActiveFeeVouchersAtom)
      .filter((v) => v.applicableProducts?.includes('SPOT'))
      .reduce((acc, v) => acc.plus(Num(v.remainingAmount)), Num(0)),
  );

  return {
    isLoadingAtom,
    vouchersAtom,
    feeVouchersAtom,
    vipVouchersAtom,
    feeTotalCountAtom,
    vipTotalCountAtom,
    perpFeeVoucherAvailableAmountAtom,
    spotFeeVoucherAvailableAmountAtom,
  };
});
activeVouchersBunja.debugLabel = 'activeVouchersBunja';

const PAST_VOUCHER_DAYS_LIMIT = 30;

const getEndedAt = (voucher: FeeVoucherGrant | VipVoucherGrant): string => {
  if ('revokedTime' in voucher && voucher.revokedTime) return voucher.revokedTime;
  if ('trialEndTime' in voucher && voucher.trialEndTime) return voucher.trialEndTime;
  return voucher.expirationTime ?? voucher.createdTime;
};

const isWithinDaysLimit = (timestamp: string, days: number): boolean => {
  return differenceInDays(new Date(), timestampToDate(timestamp)) <= days;
};

export const pastVouchersBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const vouchersBaseAtom = atomWithQuery((get) => ({
    queryKey: [...voucherQueryKey, 'past'],
    queryFn: () => getVouchersApi({ status: pastStatuses }),
    enabled: Boolean(get(isCookieLoggedInAtom)),
  }));

  const isLoadingAtom = atom((get) => get(vouchersBaseAtom).isPending);

  const vouchersAtom = atom((get) => {
    const vouchers = get(vouchersBaseAtom).data?.data.vouchers ?? [];
    return vouchers
      .filter((v) => isWithinDaysLimit(getEndedAt(v), PAST_VOUCHER_DAYS_LIMIT))
      .sort((a, b) => new Date(getEndedAt(b)).getTime() - new Date(getEndedAt(a)).getTime());
  });

  return {
    isLoadingAtom,
    vouchersAtom,
  };
});
pastVouchersBunja.debugLabel = 'pastVouchersBunja';

export const voucherActivateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);

  const mutationAtom = atomWithMutation((get) => ({
    mutationFn: ({ grantId, type }: { grantId: number; type: VoucherType }) => postVoucherActivateApi(type, grantId),
    onSettled: () => get(queryClientAtom).invalidateQueries({ queryKey: voucherQueryKey }),
    onError: (error) => snackbar.error({ message: getErrorMessage(error) }),
  }));

  const isActivatingAtom = atom((get) => get(mutationAtom).isPending);

  const activate = async (grantId: number, type: VoucherType) => {
    await store.get(mutationAtom).mutateAsync({ grantId, type });
  };

  return { activate, isActivatingAtom };
});
voucherActivateBunja.debugLabel = 'voucherActivateBunja';
